import { useMemo } from 'react';

interface Props {
  source: string;
  className?: string;
  /** Max-Height per CSS-Klasse (z.B. 'max-h-[500px]'). */
  maxHeightClass?: string;
  /** Highlight: bei true werden Keys/Strings/Comments eingefärbt. */
  highlight?: boolean;
  /** Zeigt Zeilennummern links. */
  showLineNumbers?: boolean;
}

/**
 * YamlViewer - read-only YAML-Anzeige mit minimalem Syntax-Highlighting.
 *
 * Bewusst KEIN Shiki/Prism-Dependency: zählt 100kb+ und das Tool ist lokal.
 * Statt-dessen: einfacher Tokenizer mit Regex-Pattern für:
 *   - Comments (# ...)            -> grau-italic
 *   - Keys (`foo:`)               -> primary
 *   - Strings ("..." / '...')     -> success-green
 *   - Boolean / null / numbers    -> warning-orange
 *   - Document-Marker (`---`)     -> muted
 *
 * Das ist visuell "gut genug" und vollständig SSR-faehig.
 */
export function YamlViewer({
  source,
  className,
  maxHeightClass = 'max-h-[600px]',
  highlight = true,
  showLineNumbers = true,
}: Props): JSX.Element {
  const lines = useMemo(() => source.split('\n'), [source]);

  return (
    <div
      className={[
        'overflow-auto rounded-md border border-border bg-[#0b0d12] font-mono text-xs leading-relaxed text-zinc-200',
        maxHeightClass,
        className ?? '',
      ].join(' ')}
      data-testid="yaml-viewer"
    >
      <pre className="m-0 p-0">
        <code className="block">
          {lines.map((line, idx) => (
            <div key={idx} className="flex min-h-[1.4em] hover:bg-white/5">
              {showLineNumbers && (
                <span
                  aria-hidden="true"
                  className="sticky left-0 inline-block w-10 shrink-0 select-none border-r border-zinc-800 bg-[#0b0d12] py-0.5 pr-2 text-right text-zinc-500"
                >
                  {idx + 1}
                </span>
              )}
              <span className="grow whitespace-pre py-0.5 pl-3 pr-2">
                {highlight ? renderHighlightedLine(line) : line || ' '}
              </span>
            </div>
          ))}
        </code>
      </pre>
    </div>
  );
}

/**
 * Heuristischer YAML-Tokenizer. Bewusst tolerant: bei Edge-Cases
 * fällt das Highlighting einfach auf "kein Highlight" zurück.
 */
function renderHighlightedLine(line: string): React.ReactNode {
  if (line.length === 0) return ' ';

  // Document-Marker
  if (/^---\s*$/.test(line)) {
    return <span className="text-zinc-500">{line}</span>;
  }

  // Comment-Only
  const commentMatch = line.match(/^(\s*)(#.*)$/);
  if (commentMatch) {
    return (
      <>
        <span>{commentMatch[1]}</span>
        <span className="italic text-zinc-500">{commentMatch[2]}</span>
      </>
    );
  }

  // Inline-Comment splitten (aber nur außerhalb von Strings).
  const stripped = stripInlineComment(line);
  const codePart = stripped.code;
  const commentPart = stripped.comment;

  // Key: Value oder Key:
  const kvMatch = codePart.match(/^(\s*-?\s*)([^:#\n]+?)(:)(\s*)(.*)$/);
  if (kvMatch) {
    const [, lead, key, colon, ws, rest] = kvMatch;
    return (
      <>
        <span>{lead}</span>
        <span className="text-sky-400">{key}</span>
        <span className="text-zinc-400">{colon}</span>
        <span>{ws}</span>
        {renderValueToken(rest ?? '')}
        {commentPart && <span className="italic text-zinc-500">{commentPart}</span>}
      </>
    );
  }

  // Listen-Item
  const listMatch = codePart.match(/^(\s*-)(\s+)(.*)$/);
  if (listMatch) {
    const [, dash, ws, rest] = listMatch;
    return (
      <>
        <span className="text-zinc-400">{dash}</span>
        <span>{ws}</span>
        {renderValueToken(rest ?? '')}
        {commentPart && <span className="italic text-zinc-500">{commentPart}</span>}
      </>
    );
  }

  // Fallback: kompletter Wert.
  return (
    <>
      {renderValueToken(codePart)}
      {commentPart && <span className="italic text-zinc-500">{commentPart}</span>}
    </>
  );
}

function renderValueToken(value: string): React.ReactNode {
  if (value.length === 0) return null;
  // String in Quotes
  if (/^"[^"]*"$/.test(value) || /^'[^']*'$/.test(value)) {
    return <span className="text-emerald-400">{value}</span>;
  }
  // Boolean / null
  if (/^(true|false|yes|no|null|~)\s*$/i.test(value)) {
    return <span className="text-amber-400">{value}</span>;
  }
  // Number
  if (/^-?\d+(\.\d+)?\s*$/.test(value)) {
    return <span className="text-amber-400">{value}</span>;
  }
  // Jinja-Template
  if (/\{\{.*\}\}/.test(value)) {
    const parts = value.split(/(\{\{[^}]+\}\})/g);
    return parts.map((p, i) =>
      p.startsWith('{{') ? (
        <span key={i} className="text-fuchsia-400">
          {p}
        </span>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  }
  return <span>{value}</span>;
}

function stripInlineComment(line: string): { code: string; comment: string } {
  // Naive: erster `#` außerhalb eines String-Literals.
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"' && !inSingle) inDouble = !inDouble;
    else if (c === "'" && !inDouble) inSingle = !inSingle;
    else if (c === '#' && !inSingle && !inDouble) {
      // Comment darf nicht direkt nach einem Wort kommen ohne Space (#tag != Comment-Marker).
      if (i === 0 || /\s/.test(line[i - 1] ?? '')) {
        return { code: line.slice(0, i), comment: line.slice(i) };
      }
    }
  }
  return { code: line, comment: '' };
}
